/**
 * "Move up posts from…" — an audience BOOST signal, as a block.
 *
 * Moves posts whose author the reader's graph vouches for up the page, and
 * removes nothing. Pins inside a Page Layer only. Several boost blocks in one
 * layer become ONE boost on the wire — a blend of signals — and the two dials
 * that belong to the blend rather than to any one signal (how far the boost may
 * move the page, and how the signals combine) live on the layer.
 *
 * A boost reorders the ~30 posts already fetched and cannot reach further into
 * the pool. Only a filter changes what is fetched; the layer's copy says so.
 */

import { ArrowUpIcon } from "lucide-react";
import { useCallback } from "react";
import {
  Editor,
  RecordProps,
  T,
  TLShape,
  TLShapeUtilCanBindOpts,
  useEditor,
  useValue,
} from "tldraw";

import {
  BlockBody,
  BlockContainer,
  BlockContainerShapeContext,
  InfoHint,
  NodeBlockHeader,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasPercentageInput,
  CanvasSimpleSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  AUDIENCE_BLOCK_MIN_W,
  layerChildren,
  layerFacets,
  parentLayerOf,
} from "@/components/canvas-editor/layers";
import {
  NodeDocsDescription,
  NodeDocsHeader,
  useExampleShape,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  AudienceBoostProps,
  AudienceBoostShape,
  audienceBoostType,
  Dimensions,
  isAudienceBoostShape,
  isLayerShape,
  layerType,
  metadataType,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import {
  blendableLensFacetOptions,
  blendableLensFacets,
  MAX_LENS_BLEND_ENTRIES,
  type BlendableLensFacet,
} from "@/lib/lens";

function AudienceBoostDocs({ editor }: { editor: Editor }) {
  const example = useExampleShape(new AudienceBoostShapeUtil(editor), {
    w: AUDIENCE_BLOCK_MIN_W,
  });
  return (
    <div className="w-[400px]">
      <NodeDocsHeader>{AudienceBoostShapeUtil.docs.title}</NodeDocsHeader>
      <NodeDocsDescription>
        Moves posts from this part of the reader&rsquo;s own graph up the page.
        Nothing is added or removed. Pin it inside a Page Layer; several boosts
        in one layer blend into one signal, and the layer&rsquo;s Boost strength
        sets how far that signal may move the page. The block&rsquo;s own
        percentage is its weight within that blend, not how far posts move.
      </NodeDocsDescription>
      <AudienceBoost
        {...(example as unknown as React.ComponentProps<typeof AudienceBoost>)}
        isBound
        className="w-full relative!"
      />
    </div>
  );
}

export class AudienceBoostTool extends ToolStateNode {
  static override id = audienceBoostType;

  static override uiToolDef = (editor: Editor) => ({
    id: audienceBoostType,
    icon: ArrowUpIcon,
    label: AudienceBoostShapeUtil.docs.title,
    defaultColor: AudienceBoostShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(audienceBoostType),
    docs: () => <AudienceBoostDocs editor={editor} />,
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;
    this.editor.createShape({
      type: audienceBoostType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });
    this.editor.setCurrentTool("select");
  }
}

export class AudienceBoostShapeUtil extends BindableUtil<AudienceBoostShape> {
  static override migrations = createMigrations(audienceBoostType);
  static override type = audienceBoostType;

  static override docs = {
    defaultColor: "plum" as const,
    title: "Move Up Posts From",
    description: "Move posts from a part of the reader's own graph up the page.",
  };

  override getType() {
    return audienceBoostType;
  }

  // No `resultState` — see audience-filter.tsx.
  static override props: RecordProps<AudienceBoostShape> = {
    w: T.number,
    minW: T.number,
    h: T.number,
    isCollapsed: T.boolean.optional(),
    isReadOnly: T.boolean.optional(),
    metadata: metadataType,
    facet: T.literalEnum(...blendableLensFacets),
    weight: T.number,
  };

  override getDefaultProps() {
    return {
      w: AUDIENCE_BLOCK_MIN_W,
      minW: AUDIENCE_BLOCK_MIN_W,
      h: MIN_H,
      facet: "follows" as BlendableLensFacet,
      weight: 1,
      metadata: undefined,
    };
  }

  override canBind({
    fromShapeType,
    toShapeType,
    bindingType,
  }: TLShapeUtilCanBindOpts<AudienceBoostShape>) {
    if (bindingType === "arrow") return true;
    return (
      bindingType === "layout" &&
      fromShapeType === layerType &&
      toShapeType === audienceBoostType
    );
  }

  /**
   * Refuse a drop over the facet budget, and a second boost of a facet the
   * layer already boosts: duplicates collapse to their strongest mention on
   * save, which would silently discard the weight the creator just typed.
   */
  override canPlaceInShape(container: TLShape, shape: TLShape) {
    if (!super.canPlaceInShape(container, shape)) return false;
    // Directly on a layer, never inside an audience group: a boost blends
    // rather than decides, so it has no place in an any/all.
    if (!isLayerShape(container)) return false;
    const facet = (shape as AudienceBoostShape).props.facet;
    const alreadyBoosted = layerChildren(this.editor, container).some(
      (c) => c.id !== shape.id && isAudienceBoostShape(c) && c.props.facet === facet,
    );
    if (alreadyBoosted) return false;
    const facets = layerFacets(this.editor, container, shape.id);
    facets.add(facet);
    return facets.size <= MAX_LENS_BLEND_ENTRIES;
  }

  override component(shape: AudienceBoostShape) {
    const isSelected = this.editor.getSelectedShapeIds().includes(shape.id);
    return (
      <AudienceBoost
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={Boolean(parentLayerOf(this.editor, shape))}
        isSelected={isSelected}
      />
    );
  }
}

function AudienceBoost({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected?: boolean;
  isBound: boolean;
  shape: AudienceBoostShape;
  className?: string;
  updateShape: (shape: AudienceBoostShape, props: Partial<AudienceBoostProps>) => void;
}) {
  const editor = useEditor();
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => updateShape(shape, dimensions),
    [shape, updateShape],
  );

  // Facets a sibling boost already names are hidden rather than allowed and
  // merged — see `canPlaceInShape`. Reactive, so a sibling changing its facet
  // frees the option here.
  const taken = useValue(
    "sibling-boost-facets",
    () => {
      const layer = parentLayerOf(editor, shape);
      if (!layer) return new Set<BlendableLensFacet>();
      return new Set(
        layerChildren(editor, layer)
          .filter((c) => c.id !== shape.id && isAudienceBoostShape(c))
          .map((c) => (c as AudienceBoostShape).props.facet),
      );
    },
    [editor, shape.id],
  );

  // A weight only means something against sibling boosts: with one block it is
  // redundant with the layer's Boost strength under "max" and cancels out
  // entirely under "sum". So a sole block shows the dial only when it is already
  // off 100% — the creator who set it can put it back, and nobody else is
  // offered a knob that does nothing they would want.
  const soleBoost = taken.size === 0;
  const showWeight = !soleBoost || shape.props.weight !== 1;

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        isBound={isBound}
        pattern={shape.props.metadata?.pattern}
        className={className}
        variant={shape.props.metadata?.color || "plum"}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader props={shape.props} docs={AudienceBoostShapeUtil.docs} />
        <BlockBody className="flex-col gap-2 p-3">
          {/* Wraps so the weight and its hint drop to a second line at the block's
              minimum width rather than squeezing the audience select to nothing. */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground shrink-0 text-sm">posts from</span>
            <CanvasSimpleSelect
              aria-label="Boost audience"
              value={shape.props.facet}
              options={blendableLensFacetOptions.filter(
                (o) => o.value === shape.props.facet || !taken.has(o.value),
              )}
              className="min-w-40 flex-1"
              onValueChange={(v) => updateShape(shape, { facet: v as BlendableLensFacet })}
            />
            {showWeight && (
            <>
            <span className="text-muted-foreground flex shrink-0 items-center gap-1 text-xs">
              weight
              <InfoHint label="What this weight does">
                <div className="flex max-w-xs flex-col gap-2">
                  <p>
                    How much this audience counts against the other &ldquo;Move up&rdquo;
                    blocks in the same layer when their signals combine. 100% is full weight;
                    50% counts half as much as a block at 100%.
                  </p>
                  <p>
                    It does not set how far posts move. That is the layer&rsquo;s Boost
                    strength, one dial for all its blocks. With a single &ldquo;Move up&rdquo;
                    block, leave this at 100%.
                  </p>
                </div>
              </InfoHint>
            </span>
            <CanvasPercentageInput
              aria-label={`Weight for ${shape.props.facet}`}
              value={shape.props.weight}
              className="w-20"
              onValueChange={(v) => updateShape(shape, { weight: Math.min(1, Math.max(0, v)) })}
            />
            </>
            )}
          </div>
          {soleBoost && showWeight && (
            <p className="text-xs text-amber-600 dark:text-amber-500">
              This is the only &ldquo;Move up&rdquo; block here, so a weight under 100% only
              weakens the boost. Set it to 100% and use the layer&rsquo;s Boost strength.
            </p>
          )}
          {!isBound && (
            <p className="text-muted-foreground text-xs">
              Drag this into a Page Layer to apply it.
            </p>
          )}
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
